/**
 * Transfer Detection Engine
 *
 * Identifies transactions that are transfers between accounts (not real income/expenses).
 * Examples: Mel sends to Mark's Cash App, internal account moves, Zelle between family.
 *
 * These should NOT count toward income or expense totals.
 */

// Known transfer patterns - description substrings that indicate money moving between accounts
const TRANSFER_PATTERNS: RegExp[] = [
  // Cash App
  /cash\s*app/i,
  /sent\s+to\s+/i,
  /received\s+from\s+/i,
  /cash\s+out/i,
  /cash\s+in/i,

  // Zelle / P2P
  /zelle/i,
  /venmo/i,
  /paypal.*transfer/i,

  // Bank transfers
  /transfer\s+(to|from)/i,
  /xfer\s+(to|from)/i,
  /ach\s+transfer/i,
  /internal\s+transfer/i,
  /wire\s+transfer/i,

  // Account moves
  /move\s+to/i,
  /move\s+from/i,
  /account\s+transfer/i,
];

// Known people in the household - transfers between these aren't real income/expenses
const HOUSEHOLD_NAMES: string[] = [
  "mel",
  "melanie",
  "mark",
  "mark carpenter",
];

// Type field values from CSVs that ALWAYS indicate transfers (regardless of counterparty)
const ALWAYS_TRANSFER_TYPES: string[] = [
  "cash out",
  "cash in",
  "bitcoin sale",
  "bitcoin purchase",
];

// Type field values that are transfers ONLY if counterparty is a household member
const P2P_TYPES: string[] = [
  "sent p2p",
  "received p2p",
  "p2p",
];

export interface TransferDetectionResult {
  isTransfer: boolean;
  reason?: string;
}

/**
 * Detect if a transaction is a transfer between accounts.
 *
 * Rules:
 * 1. Description matches known transfer patterns (Cash App sends, Zelle, etc.)
 * 2. CSV "Type" field contains transfer keywords (Sent P2P, Received P2P, Cash Out)
 * 3. Description mentions household member names in a send/receive context
 *
 * What is NOT a transfer:
 * - Payments to businesses (even via Cash App)
 * - Deposits from employers (paychecks)
 * - ATM withdrawals (that's real spending)
 */
export function detectTransfer(
  description: string,
  csvType: string,
  amount: number
): TransferDetectionResult {
  const desc = (description || "").toLowerCase().trim();
  const type = (csvType || "").toLowerCase().trim();

  // Check CSV type field for always-transfer types (Cash Out, Bitcoin, etc.)
  for (const keyword of ALWAYS_TRANSFER_TYPES) {
    if (type.includes(keyword)) {
      return { isTransfer: true, reason: `Type: ${type}` };
    }
  }

  // P2P is only a transfer if counterparty is a household member
  // "Sent P2P" to "CrunchyBunch LLC" = real payment, not a transfer
  // "P2P" from "Melissa Carpenter" = transfer between household accounts
  for (const keyword of P2P_TYPES) {
    if (type.includes(keyword)) {
      // Check if description mentions a household member
      for (const name of HOUSEHOLD_NAMES) {
        if (desc.includes(name)) {
          return { isTransfer: true, reason: `P2P with household: ${name}` };
        }
      }
      // P2P to/from non-household = real transaction, not a transfer
      return { isTransfer: false };
    }
  }

  // Check if description mentions sending to or receiving from household members
  for (const name of HOUSEHOLD_NAMES) {
    const sendPattern = new RegExp(`(sent|send|to|from)\\s+.*${name}`, "i");
    const receivePattern = new RegExp(`${name}.*\\s+(sent|send|to|from)`, "i");
    if (sendPattern.test(desc) || receivePattern.test(desc)) {
      return { isTransfer: true, reason: `Household: ${name}` };
    }
  }

  // Check description against known transfer patterns
  for (const pattern of TRANSFER_PATTERNS) {
    if (pattern.test(desc)) {
      // Don't flag payments TO businesses via Cash App as transfers
      // "Cash App *Starbucks" is a payment, not a transfer
      if (/cash\s*app\s*\*/i.test(desc)) {
        return { isTransfer: false };
      }
      return { isTransfer: true, reason: `Pattern: ${pattern.source}` };
    }
  }

  return { isTransfer: false };
}

/**
 * Batch-detect transfers on an array of transactions.
 * Mutates the isTransfer field in place for performance.
 */
export function tagTransfers(transactions: Transaction[]): Transaction[] {
  return transactions.map((tx) => {
    const result = detectTransfer(tx.name, tx.category, tx.amount);
    return {
      ...tx,
      isTransfer: result.isTransfer,
      // Keep existing notes, append transfer reason
      notes: result.isTransfer
        ? [tx.notes, `Auto-detected transfer: ${result.reason}`]
            .filter(Boolean)
            .join(" | ")
        : tx.notes,
    };
  });
}

/**
 * Split transactions into real transactions and transfers.
 * Use this for accurate income/expense calculations.
 */
export function splitTransfers(transactions: Transaction[]): {
  real: Transaction[];
  transfers: Transaction[];
} {
  const real: Transaction[] = [];
  const transfers: Transaction[] = [];

  for (const tx of transactions) {
    if (tx.isTransfer) {
      transfers.push(tx);
    } else {
      real.push(tx);
    }
  }

  return { real, transfers };
}
